import React, { useState } from "react";
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Collapse,
    Container,
    Grid,
    Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { cardSx, sectionSubtitleSx, sectionTitleSx } from "./homeStyles";

function HomeFeatures() {
    const [expandedCards, setExpandedCards] = useState({});

    const handleToggle = (title) => {
        setExpandedCards((prev) => ({
            ...prev,
            [title]: !prev[title],
        }));
    };

    const features = [
        {
            icon: SearchIcon,
            title: "Предварительный анализ документов",
            summary:
                "Система автоматически определяет банк, проверяет структуру документов и заранее отделяет корректные файлы от тех, которые могут исказить результат сверки.",
            details:
                "Выявляет модифицированные платежные поручения, определяет количество файлов, платежей и страниц, а также помогает безопасно подготовить документы к дальнейшей обработке.",
        },
        {
            icon: SyncAltIcon,
            title: "Сверка с бухгалтерским учетом",
            summary:
                "Qarzhy сопоставляет банковские платежи с данными учета по ИИН, ФИО, счетам и суммам выплат, помогая быстро находить расхождения.",
            details:
                "Система анализирует не только итоговую сумму по сотруднику, но и внутреннюю структуру выплат: выявляет несколько счетов у одного ИИН, несколько вариантов ФИО, несколько выплат и различия в распределении сумм.",
        },
        {
            icon: PersonSearchIcon,
            title: "Сверка с кадровыми данными",
            summary:
                "Система проверяет, соответствуют ли сотрудники из банковских платежей данным кадровой базы, и помогает быстро выявлять несоответствия.",
            details:
                "Автоматически показывает статусы «НЕТ В КАДРАХ», «НЕТ В БАНКЕ», «РАЗНЫЕ ФИО» и «ОК», выделяет проблемные записи цветом и сортирует результаты по критичности.",
        },
        {
            icon: ReceiptLongIcon,
            title: "Список сотрудников и выплат из банка",
            summary:
                "Система формирует структурированный список сотрудников, ИИН, счетов и сумм выплат по банковским документам.",
            details:
                "Такой список можно использовать для самостоятельной проверки с расчетно-платежной ведомостью, кадровыми выгрузками, табелями учета рабочего времени, СКУД-системами и другими внутренними источниками организации.",
        },
        {
            icon: AutoGraphIcon,
            title: "Интеллектуальное выявление расхождений",
            summary:
                "Автоматически определяет ключевые статусы сверки и помогает быстро выделять наиболее критичные ошибки и подозрительные случаи.",
            details:
                "Система выявляет статусы «НЕТ В УЧЕТЕ», «НЕТ В БАНКЕ», «РАЗНИЦА», «РАЗНЫЕ СЧЕТА», «РАЗНОЕ РАСПРЕДЕЛЕНИЕ СУММ», «НЕСКОЛЬКО СЧЕТОВ», «РАЗНЫЕ ФИО» и «НЕСКОЛЬКО ФИО». Результаты сортируются по степени критичности, а цветовая индикация помогает быстро выделить самые важные проблемы.",
        },
        {
            icon: FileDownloadIcon,
            title: "Excel-отчеты для дальнейшей работы",
            summary:
                "По итогам обработки система формирует готовые Excel-отчеты с итоговыми показателями, статусами и детализацией по сотрудникам.",
            details:
                "Отчеты содержат суммы, счета, ФИО и результаты сверки в удобном рабочем формате с фильтрами для бухгалтерии, внутреннего контроля, аудита и последующей отработки выявленных расхождений.",
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
            <Box textAlign="center" sx={{ mb: { xs: 4, md: 5 } }}>
                <Typography variant="h3" sx={sectionTitleSx}>
                    Возможности
                </Typography>

                <Typography sx={{ ...sectionSubtitleSx, maxWidth: 900 }}>
                    Анализирует документы, выявляет критичные расхождения, сортирует результаты по
                    важности и помогает быстрее принимать решения.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {features.map((feature) => {
                    const Icon = feature.icon;
                    const isExpanded = !!expandedCards[feature.title];

                    return (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={feature.title}>
                            <Card
                                sx={{
                                    ...cardSx,
                                    height: "100%",
                                    display: "flex",
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: { xs: 2.5, md: 3 },
                                        display: "flex",
                                        flexDirection: "column",
                                        width: "100%",
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: "rgba(37,99,235,0.10)",
                                            color: "#2563eb",
                                            width: 50,
                                            height: 50,
                                            mb: 2,
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 24 }} />
                                    </Avatar>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 800,
                                            color: "#0f172a",
                                            mb: 1.1,
                                            fontSize: { xs: "1.02rem", md: "1.08rem" },
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {feature.title}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "#475569",
                                            lineHeight: 1.72,
                                            fontSize: { xs: "0.96rem", md: "0.98rem" },
                                        }}
                                    >
                                        {feature.summary}
                                    </Typography>

                                    <Collapse in={isExpanded}>
                                        <Typography
                                            sx={{
                                                color: "#64748b",
                                                lineHeight: 1.72,
                                                fontSize: { xs: "0.94rem", md: "0.96rem" },
                                                mt: 1.4,
                                            }}
                                        >
                                            {feature.details}
                                        </Typography>
                                    </Collapse>

                                    <Box sx={{ mt: "auto", pt: 2 }}>
                                        <Typography
                                            component="button"
                                            type="button"
                                            onClick={() => handleToggle(feature.title)}
                                            sx={{
                                                border: "none",
                                                background: "transparent",
                                                p: 0,
                                                cursor: "pointer",
                                                color: "#2563eb",
                                                fontWeight: 700,
                                                fontSize: "0.95rem",
                                                lineHeight: 1.2,
                                                textAlign: "left",
                                                transition: "opacity 0.2s ease",
                                                "&:hover": {
                                                    opacity: 0.8,
                                                    textDecoration: "underline",
                                                },
                                            }}
                                        >
                                            {isExpanded ? "Скрыть" : "Показать больше"}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Container>
    );
}

export default HomeFeatures;